import { themes } from 'kb-shared';
import React, { FocusEventHandler } from 'react';
import MaskedInput from 'react-maskedinput';
import styled, { css, CSSProp } from 'styled-components';

type Props = {
  label: string;
  error?: boolean;
  className?: string;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void | null;
  name?: string;
  value?: string | null;
  autoComplete?: string | null;
  spellCheck?: string | null;
  placeholder?: string | null;
  inputType?: 'default' | 'date' | 'phone' | 'calculator' | 'zip' | 'text';
  css?: CSSProp;
  mobile: boolean;
  inputMode?: string;
  pattern?: string;
  max?: number;
  step?: number;
  onBlur?: FocusEventHandler<any>;
};

interface InputCSSProps {
  error?: boolean;
}

export const Container = styled.div`
  flex-direction: column;
  align-items: flex-start;
  max-width: 340px;
  height: 58px;
  max-height: 58px;
  padding-bottom: 12px;
  box-sizing: content-box;
`;

const InputCSS = css`
  -moz-box-sizing: border-box;
  -webkit-box-sizing: border-box;
  box-sizing: border-box;
  ${themes.fonts.DomaineDisplay};
  width: 100%;
  font-size: 24px;
  color: black;
  height: 40px;
  border: 0px;
  text-align: left;
  background-color: transparent;
  transition: all 400ms ease 0s;
  border-bottom: solid 1px #000000;
  ::placeholder {
    ${themes.fonts.DomaineDisplay};
    font-size: 24px;
    text-align: left;
    color: #c6c7ce;
  }
  :focus {
    outline: 0;
    border-bottom: solid 1px ${themes.colors.KBYellow};
  }
`;

const Input = styled.input`
  ${InputCSS};
  padding-bottom: 2px;
`;

// On mobile, padding-bottom is magic number to make bottom border line match text input
const MobileDateInput = styled.input`
  ${InputCSS};
  padding-bottom: 2px;
  font-size: 20px;
`;

const StyledMaskInput = styled(MaskedInput)`
  ${InputCSS};
`;

const StyledCalculatorInput = styled(Input)<InputCSSProps>`
  color: ${({ theme }) => theme.colors.KBWhite};
  border-bottom: ${({ error, theme }) =>
    `solid 1px ${error ? theme.colors.KBError : theme.colors.KBWhite}`};
`;

const StyledZipCodeInput = styled(StyledMaskInput)<InputCSSProps>`
  border-color: ${({ error }) => (error ? '#000000' : 'inherit')};
`;

interface LabelProps {
  visible: boolean;
}

export const Label = styled.label`
  ${themes.fonts.FoundersGrotesk};
  font-size: 14px;
  transition: all 400ms ease 0s;
  ${({ visible }: LabelProps) => `
    color: ${visible ? themes.colors.KBNight : 'transparent'};
  `}
`;

function renderInput(props: Props) {
  const { label, value, onChange, onBlur, name, error, inputType, placeholder, ...rest } = props;
  if (inputType === 'date') {
    if (props.mobile) {
      // @ts-ignore
      return (
        <MobileDateInput id={label} name={name} onChange={onChange} type="date" onBlur={onBlur} />
      );
    }
    return (
      <StyledMaskInput
        id={label}
        // @ts-ignore
        name={name}
        mask="11/11/1111"
        value={value || ''}
        onChange={onChange}
        placeholder={'MM/DD/YYY'}
        placeholderChar={'-'}
        onBlur={onBlur}
      />
    );
  } else if (inputType === 'phone') {
    return (
      <StyledMaskInput
        id={label}
        // @ts-ignore
        name={name}
        mask="111-111-1111"
        value={value || ''}
        onChange={onChange}
        onBlur={onBlur}
        placeholder={'555-555-5555'}
        placeholderChar={'-'}
        type="tel"
        autoComplete="tel"
        inputMode="tel"
      />
    );
  } else if (inputType === 'calculator') {
    return (
      // @ts-ignore
      <StyledCalculatorInput
        id={label}
        // @ts-ignore
        value={value}
        // @ts-ignore
        onChange={onChange}
        // @ts-ignore
        name={name}
        // @ts-ignore
        error={error}
        // @ts-ignore
        placeholder={placeholder}
        // @ts-ignore
        {...rest}
      />
    );
  } else if (inputType === 'zip') {
    // US only zip code
    return (
      <StyledZipCodeInput
        id={label}
        name={name}
        mask="11111"
        value={value || ''}
        onChange={onChange}
        placeholder={'ZIP'}
        onBlur={onBlur}
        placeholderChar={' '}
        error={error}
        type="text"
      />
    );
  }
  return (
    // @ts-ignore
    <Input
      id={label}
      // @ts-ignore
      value={value}
      onChange={onChange}
      onBlur={onBlur}
      // @ts-ignore
      name={name}
      // @ts-ignore
      error={error}
      // @ts-ignore
      placeholder={placeholder}
      {...rest}
    />
  );
}

const TextInput = (props: Props) => {
  const { label, className } = props;
  return (
    <Container className={className}>
      <Label htmlFor={label} visible={true}>
        {label}
      </Label>
      {renderInput(props)}
    </Container>
  );
};

TextInput.defaultProps = {
  label: '',
  error: false,
  onChange: null,
  onBlur: null,
  name: '',
  value: '',
  type: 'default',
  mobile: false
};

interface StripeContainerProps {
  width?: number | string;
}

/**
 * Stripe Specific Input Wrapper
 * On Stripe elements we can't use our TextInput
 * and are limited in what styles we can apply to their elements.
 */
const StripeContainer = styled(Container)`
  padding-bottom: 5px;
  border-bottom: solid 1px black;
  ${(props: StripeContainerProps) => `width: ${props.width || 0}`};
`;

const StripeChildWrapper = styled.div`
  padding-top: 10px;
`;

type StripeLabelProps = {
  type: string;
  label: string;
  width?: string; //specified as '80px'
  children: React.ReactNode | null;
};

export const StripeInputWrapper = (props: StripeLabelProps) => {
  const { label, type, children, width } = props;
  const renderLabel = () => (
    // @ts-ignore
    <Label htmlFor={label} visible={true} type={type}>
      {label}
    </Label>
  );
  return (
    <StripeContainer width={width}>
      {renderLabel()}
      <StripeChildWrapper>{children}</StripeChildWrapper>
    </StripeContainer>
  );
};

StripeInputWrapper.defaultProps = {
  type: '',
  label: '',
  width: 'auto',
  children: null
};

export const stripeInputStyle = {
  base: {
    fontFamily: 'DomaineDisplay',
    fontDisplay: 'swap',
    fontSize: '24px',
    color: 'black',
    textAlign: 'left',
    '::placeholder': {
      fontFamily: 'DomaineDisplay',
      fontDisplay: 'swap',
      fontSize: '24px',
      textAlign: 'left',
      color: '#c6c7ce'
    }
  }
};

export default TextInput;
